import { useState } from "react";
import { useLocation } from "react-router";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";

import Link from "../../atoms/mui/Link";
import ExplorerTopBar from "./ExplorerTopBar";
import UpgradeBanner from "../upgrade/UpgradeBanner";

import {
  sideNavGroups,
  NAVIGATOR_PREVIEW,
  NAVIGATOR_UPGRADE_PATH,
  PENDING_GATE_MESSAGES
} from "../helpers";
import { usePendingGateActivity } from "../../../helpers/components/explorerPendingGates";

const SIDEBAR_WIDTH = 248;

const useActiveMatcher = () => {
  const { pathname, search } = useLocation();
  const currentView = new URLSearchParams(search).get("view");

  return (itemPath) => {
    const [itemPathname, itemQuery] = itemPath.split("?");
    if (itemQuery) {
      const itemView = new URLSearchParams(itemQuery).get("view");
      return pathname === itemPathname && currentView === itemView;
    }
    if (itemPathname === "/explorer/dashboard") {
      return pathname === "/explorer/dashboard" && !currentView;
    }
    return pathname.startsWith(itemPathname);
  };
};

const SidebarLogo = () => {
  return (
    <Box display="flex" alignItems="center" gap={1} px={2} py={2.5}>
      <Typography variant="h6" color="inherit">
        Aclymate
      </Typography>
      <Box
        px={1}
        py={0.25}
        sx={{
          borderRadius: "6px",
          backgroundColor: whiteAlpha(0.18)
        }}
      >
        <Typography variant="caption" color="inherit">
          Explorer
        </Typography>
      </Box>
    </Box>
  );
};

const NavRow = ({ icon, title, selected }) => (
  <Box
    py={1}
    px={2}
    mx={1}
    display="flex"
    alignItems="center"
    gap={1.5}
    sx={{
      borderRadius: "8px",
      backgroundColor: selected ? blackAlpha(0.28) : "transparent",
      "&:hover": {
        backgroundColor: selected ? blackAlpha(0.28) : whiteAlpha(0.1)
      }
    }}
  >
    <Box display="flex" justifyContent="center" style={{ width: 18 }}>
      <FontAwesomeIcon icon={icon} style={{ fontSize: 15 }} />
    </Box>
    <Box flexGrow={1} minWidth={0}>
      <Typography variant="body2" color="inherit" noWrap>
        {title}
      </Typography>
    </Box>
  </Box>
);

const SideNavItem = ({ item, isActive }) => (
  <Link href={item.path} color="inherit" styleProps={{ color: "inherit" }}>
    <NavRow icon={item.icon} title={item.title} selected={isActive(item.path)} />
  </Link>
);

const NavGroupLabel = ({ label }) => (
  <Box px={3} pt={2} pb={0.5} color={whiteAlpha(0.6)}>
    <Typography variant="overline" color="inherit">
      {label}
    </Typography>
  </Box>
);

const DISABLED_OPACITY = 0.35;

// hexToRgba (@aclymatepackages/converters) can't parse 3-digit hex, and MUI's
// default palette.common.white/black are "#fff"/"#000" — it silently emits
// invalid rgba that the browser drops, so alpha fills need literals here.
const whiteAlpha = (opacity) => `rgba(255, 255, 255, ${opacity})`;
const blackAlpha = (opacity) => `rgba(0, 0, 0, ${opacity})`;

const LockedPillarLabel = ({ label, icon, onClick }) => {
  const { palette } = useTheme();
  return (
    <Box
      onClick={onClick}
      px={3}
      pt={2}
      pb={0.5}
      display="flex"
      alignItems="center"
      gap={1}
      sx={{
        cursor: "pointer",
        color: whiteAlpha(DISABLED_OPACITY),
        "&:hover": { color: palette.common.white }
      }}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: 11 }} />
      <Typography variant="overline" color="inherit">
        {label}
      </Typography>
      <FontAwesomeIcon icon={faLock} style={{ fontSize: 10 }} />
    </Box>
  );
};

const PendingGateNavItem = ({ item }) => (
  <Tooltip title={PENDING_GATE_MESSAGES[item.pendingGate] || ""} placement="right">
    <Box
      py={1}
      px={2}
      mx={1}
      display="flex"
      alignItems="center"
      gap={1.5}
      sx={{
        borderRadius: "8px",
        cursor: "default",
        color: whiteAlpha(DISABLED_OPACITY)
      }}
    >
      <Box display="flex" justifyContent="center" style={{ width: 18 }}>
        <FontAwesomeIcon icon={item.icon} style={{ fontSize: 15 }} />
      </Box>
      <Box flexGrow={1} minWidth={0}>
        <Typography variant="body2" color="inherit" noWrap>
          {item.title}
        </Typography>
      </Box>
    </Box>
  </Tooltip>
);

const PreviewModal = ({ content, onClose }) => {
  const { palette } = useTheme();

  if (!content) {
    return null;
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="overline" color="secondary">
          Navigator preview
        </Typography>
        <Typography variant="h6" color="textPrimary">
          {content.title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          mb={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            height: 160,
            borderRadius: "8px",
            backgroundColor: palette.backgroundGray.main
          }}
        >
          <Typography variant="caption" color="textSecondary">
            Screenshot placeholder — supplied by marketing
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary">
          {content.blurb}
        </Typography>
        <Box mt={1.5} display="flex" flexDirection="column" gap={1}>
          {content.features.map((feature) => (
            <Box key={feature} display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: palette.secondary.main,
                  flexShrink: 0
                }}
              />
              <Typography variant="body2" color="textPrimary">
                {feature}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Link
          href={NAVIGATOR_UPGRADE_PATH}
          styleProps={{ textDecoration: "none" }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={onClose}
            sx={{ color: palette.common.white }}
          >
            Upgrade to Navigator
          </Button>
        </Link>
      </DialogActions>
    </Dialog>
  );
};

const UpgradePromo = () => {
  const { palette } = useTheme();
  return (
    <Box
      m={2}
      p={2}
      sx={{
        borderRadius: "12px",
        backgroundColor: whiteAlpha(0.12)
      }}
    >
      <Typography variant="subtitle2" color="inherit">
        Unlock the full Navigator
      </Typography>
      <Box mt={0.5} mb={1.5}>
        <Typography variant="caption" color="inherit">
          Advanced planning, deeper insights, and certification workflows.
        </Typography>
      </Box>
      <Link
        href={NAVIGATOR_UPGRADE_PATH}
        styleProps={{ textDecoration: "none", color: "inherit" }}
      >
        <Box
          py={1}
          sx={{
            borderRadius: "8px",
            textAlign: "center",
            backgroundColor: palette.common.white
          }}
        >
          <Typography variant="button" color="secondary">
            Upgrade to Navigator
          </Typography>
        </Box>
      </Link>
    </Box>
  );
};

const renderGroup = ({ group, idx, isActive, openPreview, pendingGateActivity }) => {
  if (group.locked) {
    return (
      <LockedPillarLabel
        key={`locked-${group.label}`}
        label={group.label}
        icon={group.icon}
        onClick={() => openPreview(NAVIGATOR_PREVIEW[group.previewKey])}
      />
    );
  }

  return (
    <Box key={group.label || `group-${idx}`} mb={0.5}>
      {group.label && <NavGroupLabel label={group.label} />}
      {group.items.map((item) =>
        item.pendingGate && !pendingGateActivity[item.pendingGate] ? (
          <PendingGateNavItem key={item.title} item={item} />
        ) : (
          <SideNavItem key={item.title} item={item} isActive={isActive} />
        )
      )}
    </Box>
  );
};

const SidebarContent = () => {
  const isActive = useActiveMatcher();
  const [preview, setPreview] = useState(null);
  const pendingGateActivity = usePendingGateActivity();

  return (
    <Box display="flex" flexDirection="column" style={{ height: "100%" }}>
      <SidebarLogo />
      <Box
        flexGrow={1}
        sx={{
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" }
        }}
      >
        {sideNavGroups.map((group, idx) =>
          renderGroup({
            group,
            idx,
            isActive,
            openPreview: setPreview,
            pendingGateActivity
          })
        )}
      </Box>
      <UpgradePromo />
      <PreviewModal content={preview} onClose={() => setPreview(null)} />
    </Box>
  );
};

const DashboardLayout = ({ title, children, hideUpgradeBanner = false }) => {
  const { palette } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);

  const sidebarSx = {
    width: SIDEBAR_WIDTH,
    backgroundColor: palette.primary.dark,
    color: palette.common.white
  };

  return (
    <Box
      style={{ width: "100%", height: "100vh" }}
      display="flex"
      alignItems="stretch"
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { ...sidebarSx, boxSizing: "border-box" }
        }}
      >
        <SidebarContent />
      </Drawer>

      <Box
        sx={{
          display: { xs: "none", md: "block" },
          flexShrink: 0,
          ...sidebarSx
        }}
      >
        <SidebarContent />
      </Box>

      <Box
        flexGrow={1}
        display="flex"
        flexDirection="column"
        style={{ minWidth: 0, background: palette.backgroundGray.light }}
      >
        <Box sx={{ position: "sticky", top: 0, zIndex: 1100 }}>
          <ExplorerTopBar
            title={title}
            onMenuClick={() => setMobileOpen(true)}
          />
          {!hideUpgradeBanner && <UpgradeBanner />}
        </Box>
        <Box
          sx={{ overflowY: "auto", overflowX: "hidden", flexGrow: 1 }}
          px={{ xs: 2, md: 4 }}
          pt={2}
          pb={4}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
